export type ChatSSEvent = 'ready' | 'finish' | 'error' | 'keepalive' | 'status' | 'keep-alive' | 'itinerary';

export type ChatEventHandler = (event: ChatSSEvent, payload: unknown) => void;

export interface ChatStreamParams {
  url: string; // e.g. http://localhost:4000/chat-stream or your Function URL
  accessToken: string;
  body: { tripProfileId: string; message: string };
  signal?: AbortSignal;
  onEvent?: ChatEventHandler;
}

export async function chatStream({ url, body, accessToken, signal, onEvent }: ChatStreamParams): Promise<string> {
  console.log('*****', body);

  const cacheBustedUrl = url.includes('?') ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`;

  const res = await fetch(cacheBustedUrl, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(`SSE request failed: ${res.status} ${res.statusText} ${text}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let aggregated = '';
  let finished = false;

  const emit = (event: ChatSSEvent, data: unknown) => {
    try {
      onEvent?.(event, data);
      console.log('SSE 41', 'Event:', event, 'Data:', data);
    } catch {
      // no-op
    }
  };

  try {
    while (true) {
      if (finished) break;
      const { value, done } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        if (!frame.trim()) continue;

        let event: ChatSSEvent | undefined;
        let dataRaw = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) {
            event = line.slice(6).trim() as ChatSSEvent;
          } else if (line.startsWith('data:')) {
            dataRaw += (dataRaw ? '\n' : '') + line.slice(5).trim();
          }
        }

        if (!event) continue;

        let payload: unknown = dataRaw;
        try {
          payload = JSON.parse(dataRaw);
        } catch {
          // keep as string if not JSON
        }

        emit(event, payload);

        if (event === 'itinerary') {
          try {
            aggregated = typeof payload === 'string' ? payload : JSON.stringify(payload);
          } catch {
            // ignore stringify errors
          }
          finished = true;
        }

        if (event === 'finish' || event === 'error') {
          finished = true;
        }
      }

      if (finished) {
        console.log('sse-chat emit stream finished. reader.cancel()');
        try {
          await reader.cancel();
        } catch {
          // ignore cancel errors
        }
        break;
      }
    }
  } catch (err: unknown) {
    // If aborted, treat as normal termination
    if (err && typeof err === 'object' && 'name' in err && (err as { name?: string }).name !== 'AbortError') {
      throw err;
    }
  }

  // Only flush trailing decoder state if we didn't already finish
  if (!finished) {
    const tail = decoder.decode();
    if (tail) {
      buffer += tail;
      const frames = buffer.split('\n\n');
      for (const frame of frames) {
        if (!frame.trim()) continue;

        let event: ChatSSEvent | undefined;
        let dataRaw = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) {
            event = line.slice(6).trim() as ChatSSEvent;
          } else if (line.startsWith('data:')) {
            dataRaw += (dataRaw ? '\n' : '') + line.slice(5).trim();
          }
        }

        if (!event) continue;

        let payload: unknown = dataRaw;
        try {
          payload = JSON.parse(dataRaw);
        } catch {
          // ignore
        }

        emit(event, payload);

        if (event === 'itinerary') {
          try {
            aggregated = typeof payload === 'string' ? payload : JSON.stringify(payload);
          } catch {
            // ignore
          }
        }
      }
    }
  }

  return aggregated;
}
